import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardContent, CardFooter } from "components/ui/card";
import { Button } from "components/ui/button";
import { useI18n } from "i18n";
import LanguageSwitcher from "components/LanguageSwitcher";
import { EmailStep, MfaStep, PasswordStep } from "components/auth/LoginSteps";
import portalApi, { setPortalToken } from "lib/portalApi";

// /pub/login — the SAME login layout as the console (components/auth/LoginSteps): e-mail -> MFA
// -> password, plus the face button on the first step and a "create account" link. Only the
// flow differs: the portal is token-based, so each step redeems a signed step token instead of
// a session cookie.
export default function PortalLogin() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [stepToken, setStepToken] = useState(null);
  const [email, setEmail] = useState("");
  const [mfaCode, setMfaCode] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [faceLoading, setFaceLoading] = useState(false);
  const [faceToken, setFaceToken] = useState(null);
  const pollRef = useRef(null);

  const land = useCallback((data) => {
    setPortalToken(data.token);
    navigate(data.face_required ? "/pub/enroll" : "/pub", { replace: true });
  }, [navigate]);

  const goToStep = (target) => {
    setError("");
    if (target < step) {
      if (target <= 2) setPassword("");
      if (target <= 1) setMfaCode(["", "", "", "", "", ""]);
    }
    setStep(target);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await portalApi.post("/login/email/", { email: email.trim() });
      setStepToken(r.data.step_token);
      setStep(2);
    } catch {
      setError(t("login.email.error", t("pub.login.invalid", "E-mail inválido.")));
    } finally { setLoading(false); }
  };

  const handleMfaSubmit = async (codeStr) => {
    const code = typeof codeStr === "string" ? codeStr : mfaCode.join("");
    setError(""); setLoading(true);
    try {
      const r = await portalApi.post("/login/mfa/", { step_token: stepToken, code });
      setStepToken(r.data.step_token);
      setStep(3);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d === "invalid_session") { goToStep(1); setError(t("pub.login.expired", "A sessão de login expirou. Comece de novo.")); }
      else { setMfaCode(["", "", "", "", "", ""]); setError(t("login.mfaInvalid", "Código inválido. Tente novamente.")); }
    } finally { setLoading(false); }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await portalApi.post("/login/password/", { step_token: stepToken, password });
      land(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d === "invalid_session") { goToStep(1); setError(t("pub.login.expired", "A sessão de login expirou. Comece de novo.")); }
      else setError(t("error.wrongPassword", "Senha incorreta."));
    } finally { setLoading(false); }
  };

  // Face sign-in — open the hosted capture and poll for the verdict.
  const handleFaceLogin = async () => {
    setFaceLoading(true); setError("");
    try {
      const r = await portalApi.post("/login/face/start/", {});
      setFaceToken(r.data.session_token);
      window.open(r.data.verify_url, "_blank", "noopener");
    } catch {
      setError(t("pub.login.faceUnavailable", "Login facial indisponível no momento."));
    } finally { setFaceLoading(false); }
  };

  useEffect(() => {
    if (!faceToken) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const r = await portalApi.post("/login/face/result/", { session_token: faceToken });
        clearInterval(pollRef.current);
        land(r.data);
      } catch (err) {
        const d = err?.response?.data?.detail;
        if (d === "liveness_failed" || d === "not_identified" || d === "not_a_portal_account") {
          clearInterval(pollRef.current); setFaceToken(null);
          setError(d === "not_a_portal_account"
            ? t("pub.login.notPortal", "Este rosto não pertence a uma conta do portal.")
            : t("pub.login.faceFailed", "Não foi possível identificar você. Tente novamente."));
        }
      }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [faceToken, land, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7] px-4 py-8">
      <div className="w-full max-w-[420px]">
        <div className="flex justify-end mb-3">
          <LanguageSwitcher />
        </div>
        <Card className="shadow-lg border-0 bg-white rounded-2xl">
          {step === 1 && (
            <EmailStep
              email={email}
              setEmail={setEmail}
              onSubmit={handleEmailSubmit}
              loading={loading}
              error={error}
              oidcEnabled={false}
              onFaceLogin={handleFaceLogin}
              faceLoading={faceLoading || !!faceToken}
            />
          )}
          {step === 2 && (
            <MfaStep
              code={mfaCode}
              setCode={setMfaCode}
              onSubmit={(arg) => { if (arg?.preventDefault) { arg.preventDefault(); handleMfaSubmit(); } else { handleMfaSubmit(arg); } }}
              onBack={() => goToStep(1)}
              loading={loading}
              error={error}
            />
          )}
          {step === 3 && (
            <PasswordStep
              password={password}
              setPassword={setPassword}
              onSubmit={handlePasswordSubmit}
              onBack={() => goToStep(2)}
              loading={loading}
              error={error}
            />
          )}
          {step === 1 && (
            <CardFooter className="justify-center pt-0">
              <Button type="button" variant="ghost" size="sm"
                onClick={() => navigate("/pub/register")}>
                {t("pub.login.register", "Criar minha conta")}
              </Button>
            </CardFooter>
          )}
        </Card>
      </div>
    </div>
  );
}
